import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Settings,
  Shield,
  Search,
  Globe,
  Clock,
  Eye,
  EyeOff,
  Download,
  FileText,
  Play,
  Trash2,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
} from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useChartLib } from '@/components/charts/useChartLib';
import { CHART_LIBS, type ChartLib } from '@/components/charts/types';
import { useFeatureVisibility, type PreviewStage } from '@/hooks/useFeatureVisibility';
import { FeatureOverrideAdmin } from '@/components/dev-console/FeatureOverrideAdmin';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { FilterChip } from '@/components/common/FilterChip';
import { KpiStrip } from '@/components/common/KpiStrip';
import type { KpiItem } from '@/components/common/KpiStrip';
import { formatDateTime, formatTimeAgo } from '@/utils/formatters';
import { ExportMenu } from '@/components/common/ExportMenu';
import { downloadCsv, formatExportDate } from '@/utils/csv';
import { createBrandedPdf, addBrandedTable, downloadPdf, loadLogoForPdf, exportFilename } from '@/utils/pdf';
import {
  getAdminOrganizations,
  getPendingOrganizations,
  approveOrganization,
  denyOrganization,
  syncHierarchy,
  getRootConfig,
  updateOrgClassification,
  updateOrgPolling,
  updateOrgStatus,
  exportOrgData,
  purgeOrgData,
  decommissionOrganization,
  pollOrganization,
  getAuditLog,
  getAuditExportUrl,
  getPollingStatus,
  pausePolling,
  resumePolling,
  getPollingCycles,
  updatePollingEngineConfig,
  getAuth0Configs,
  upsertAuth0Config,
  deleteAuth0Config,
  getPlatformConfigs,
  upsertPlatformConfig,
  deletePlatformConfig,
  pausePlatformPolling,
  resumePlatformPolling,
  getPollingCycleOrgs,
} from '@/services/api';
import type { DevAuditEntry, PollingStatus, PollCycleLogEntry, PollCycleOrgResult, Auth0ConfigEntry } from '@/services/api';
import type { Organization, PlatformConfig } from '@/types/tenant';
import { useUIStore } from '@/stores/uiStore';
import { ApiKeysManager } from '@/components/dashboard/ApiKeysManager';

type ClassificationFilter = 'all' | 'customer' | 'partner' | 'shared' | 'internal' | 'demo' | 'unknown';
type OrgStatusFilter = 'all' | Organization['status'];
type OrgAuthFilter = 'all' | 'auth0' | 'legacy';
type OrgPollingFilter = 'all' | 'enabled' | 'disabled';
type OrgSortField =
  | 'name'
  | 'domain'
  | 'status'
  | 'classification'
  | 'authType'
  | 'nodes'
  | 'users'
  | 'polling'
  | 'lastPoll';

const CLASSIFICATION_OPTIONS = ['customer', 'partner', 'shared', 'internal', 'demo', 'unknown'] as const;

const STATUS_BADGE_CLASSES: Record<Organization['status'], string> = {
  active: 'dls-bg-status-online/10 dls-text-status-online dls-border-status-online/20',
  pending_approval: 'dls-bg-status-warning/10 dls-text-status-warning dls-border-status-warning/20',
  disabled: 'dls-bg-muted dls-text-muted-foreground dls-border-border',
  decommissioned: 'dls-bg-status-offline/10 dls-text-status-offline dls-border-status-offline/20',
};

const CLASSIFICATION_BADGE_CLASSES: Record<Organization['classification'], string> = {
  customer: 'dls-bg-neeve-tertiary/20 dls-text-neeve-primary dls-border-neeve-tertiary/30',
  partner: 'dls-bg-blue-100 dls-text-blue-700 dls-border-blue-200',
  shared: 'dls-bg-purple-100 dls-text-purple-700 dls-border-purple-200',
  internal: 'dls-bg-muted dls-text-muted-foreground dls-border-border',
  demo: 'dls-bg-status-warning/10 dls-text-status-warning dls-border-status-warning/20',
  unknown: 'dls-bg-muted dls-text-muted-foreground dls-border-border',
};

export function DevConsole() {
  // Data state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [pendingOrgs, setPendingOrgs] = useState<Organization[]>([]);
  const [rootConfig, setRootConfig] = useState<{
    isConfigured: boolean;
    portalUrl: string | null;
    pollingIntervalSeconds: number | null;
    lastHierarchySyncAt: string | null;
    isActive: boolean;
  } | null>(null);
  const [auth0Configs, setAuth0Configs] = useState<Auth0ConfigEntry[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [classificationFilter, setClassificationFilter] = useState<ClassificationFilter>('all');
  const [orgStatusFilter, setOrgStatusFilter] = useState<OrgStatusFilter>('all');
  const [orgAuthFilter, setOrgAuthFilter] = useState<OrgAuthFilter>('all');
  const [orgPollingFilter, setOrgPollingFilter] = useState<OrgPollingFilter>('all');
  const [orgSortBy, setOrgSortBy] = useState<OrgSortField>('name');
  const [orgSortDir, setOrgSortDir] = useState<'asc' | 'desc'>('asc');

  // Organizations bulk-select state
  const [orgSelectedIds, setOrgSelectedIds] = useState<Set<string>>(new Set());
  const [orgBulkClassification, setOrgBulkClassification] = useState<string>('customer');
  const [orgBulkProcessing, setOrgBulkProcessing] = useState(false);
  const [orgBulkResult, setOrgBulkResult] = useState<string | null>(null);

  // Pending approvals UI state
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingClassFilter, setPendingClassFilter] = useState<ClassificationFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkClassification, setBulkClassification] = useState<string>('customer');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Audit trail state
  const [auditLogs, setAuditLogs] = useState<DevAuditEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);

  // Per-org polling state
  const [pollingOrgId, setPollingOrgId] = useState<string | null>(null);
  const [pollResult, setPollResult] = useState<string | null>(null);

  // Polling controls state
  const [pollingStatus, setPollingStatus] = useState<PollingStatus | null>(null);
  const [pollingCycles, setPollingCycles] = useState<PollCycleLogEntry[]>([]);
  const [pollingCyclesTotal, setPollingCyclesTotal] = useState(0);
  const [cyclesPage, setCyclesPage] = useState(1);
  const CYCLES_PER_PAGE = 100;
  const [expandedCycleId, setExpandedCycleId] = useState<string | null>(null);
  const [cycleOrgResults, setCycleOrgResults] = useState<PollCycleOrgResult[]>([]);
  const [loadingOrgResults, setLoadingOrgResults] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);

  // Reactivation dialog state
  const [reactivateOrg, setReactivateOrg] = useState<Organization | null>(null);
  const [decommissionOrg, setDecommissionOrg] = useState<Organization | null>(null);

  // Polling config editing state
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({
    orgBatchSize: 5,
    perPortalRateLimit: 60,
    perPortalBurstSize: 10,
    maxCycleDurationSec: 270,
    statsEnabled: true,
    statsBatchSize: 10,
  });
  const [savingConfig, setSavingConfig] = useState(false);

  // Auth0 form state
  const [auth0Form, setAuth0Form] = useState({
    label: '',
    domain: '',
    clientId: '',
    clientSecret: '',
    isDefault: false,
  });
  const [showSecret, setShowSecret] = useState(false);
  const [savingAuth0, setSavingAuth0] = useState(false);

  // Platform config state
  const [platformConfigs, setPlatformConfigs] = useState<PlatformConfig[]>([]);
  const { selectedPlatformId, setSelectedPlatformId } = useUIStore();
  // Coerce null → undefined for API calls
  const activePlatformId = selectedPlatformId ?? undefined;
  const [showPlatformDialog, setShowPlatformDialog] = useState(false);
  const [platformForm, setPlatformForm] = useState({
    label: '',
    slug: '',
    rootPortalUrl: '',
    portalUrlSuffix: '',
    apiKey: '',
    auth0ConfigId: '' as string,
  });
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [deletingPlatformId, setDeletingPlatformId] = useState<string | null>(null);

  // Fetch all data — each call is independent so partial failures don't block
  // others. Wrapped in useCallback so its identity is stable across renders;
  // depends only on the platform-id arg, so identity is fixed (no reactive
  // closure reads). State setters are stable per React.
  const fetchData = useCallback(async (platformId?: string) => {
    setLoading(true);
    const results = await Promise.allSettled([
      getAdminOrganizations(platformId),
      getPendingOrganizations(platformId),
      getRootConfig(),
      getAuth0Configs(),
      getAuditLog(50),
      getPollingStatus(platformId),
      getPollingCycles(100, platformId),
      getPlatformConfigs(),
    ]);

    if (results[0].status === 'fulfilled') {
      setOrganizations(results[0].value.organizations);
      setOrgSelectedIds(new Set());
    }
    if (results[1].status === 'fulfilled') {
      setPendingOrgs(results[1].value.organizations);
      setSelectedIds(new Set());
    }
    if (results[2].status === 'fulfilled') {
      setRootConfig(results[2].value);
    }
    if (results[3].status === 'fulfilled') {
      setAuth0Configs(results[3].value.configs);
    }
    if (results[4].status === 'fulfilled') {
      setAuditLogs(results[4].value.logs);
      setAuditTotal(results[4].value.total);
    }
    if (results[5].status === 'fulfilled') {
      setPollingStatus(results[5].value);
    }
    if (results[6].status === 'fulfilled') {
      setPollingCycles(results[6].value.cycles);
      setPollingCyclesTotal(results[6].value.total);
      setCyclesPage(1);
    }
    if (results[7].status === 'fulfilled') {
      setPlatformConfigs(results[7].value.configs);
      // Auto-select production platform on first load
      if (!platformId && results[7].value.configs.length > 0) {
        const prod = results[7].value.configs.find(c => c.isProduction);
        if (prod) setSelectedPlatformId(prod.id);
      }
    }

    setLoading(false);
  }, [setSelectedPlatformId]);

  const initialLoadDone = useRef(false);

  useEffect(() => {
    void fetchData(activePlatformId);
    initialLoadDone.current = true;
    // Mount-only initial load; subsequent platform changes are handled by
    // the second effect below. Listing activePlatformId/fetchData here would
    // re-fire on every platform change and duplicate the second effect's job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when selected platform changes (skip initial auto-select)
  useEffect(() => {
    if (initialLoadDone.current && selectedPlatformId) {
      void fetchData(activePlatformId);
    }
  }, [selectedPlatformId, activePlatformId, fetchData]);

  // Polling Cycles drill-down
  const handleToggleCycleExpand = async (cycleId: string) => {
    if (expandedCycleId === cycleId) {
      setExpandedCycleId(null);
      setCycleOrgResults([]);
      return;
    }
    setExpandedCycleId(cycleId);
    setLoadingOrgResults(true);
    try {
      const result = await getPollingCycleOrgs(cycleId);
      setCycleOrgResults(result.results);
    } catch {
      setCycleOrgResults([]);
    } finally {
      setLoadingOrgResults(false);
    }
  };

  // Polling Cycles pagination
  const handleLoadMoreCycles = async () => {
    const nextPage = cyclesPage + 1;
    const pid = activePlatformId;
    try {
      const result = await getPollingCycles(CYCLES_PER_PAGE, pid, (nextPage - 1) * CYCLES_PER_PAGE);
      setPollingCycles((prev) => [...prev, ...result.cycles]);
      setCyclesPage(nextPage);
    } catch {
      // silently fail
    }
  };

  // Polling Cycles export handlers — fetch ALL cycles for the period
  const handleExportCyclesCsv = async () => {
    // Fetch all cycles for this platform (up to 500)
    const pid = activePlatformId;
    const allResult = await getPollingCycles(500, pid);
    const allCycles = allResult.cycles;
    if (!allCycles.length) return;
    downloadCsv(
      allCycles.map((c) => ({
        Cycle: c.cycleNumber,
        Started: formatExportDate(c.startedAt),
        Completed: formatExportDate(c.completedAt),
        'Duration (s)': c.durationMs ? (c.durationMs / 1000).toFixed(1) : '',
        Tiers: c.tiersRun.join(', '),
        Attempted: c.orgsAttempted,
        Succeeded: c.orgsSucceeded,
        Failed: c.orgsFailed,
        Skipped: c.orgsSkipped,
        Deferred: c.orgsDeferred ?? 0,
        'API Calls': c.apiCallsMade,
        Trigger: c.trigger,
      })),
      exportFilename('polling-cycles', 'csv'),
      { report: 'Polling Cycles', title: 'Neeve Insights — Dev Console' }
    );
  };

  const handleExportCyclesPdf = async () => {
    // Fetch all cycles for this platform (up to 500)
    const pid = activePlatformId;
    const allResult = await getPollingCycles(500, pid);
    const allCycles = allResult.cycles;
    if (!allCycles.length) return;
    const logoData = await loadLogoForPdf();
    const layout = createBrandedPdf({
      title: 'Polling Cycles',
      subtitle: `${allCycles.length} Cycles`,
      logoData,
    });
    addBrandedTable(
      layout.doc,
      ['Cycle', 'Started', 'Duration', 'Tiers', 'Attempted', 'Succeeded', 'Failed', 'API Calls', 'Trigger'],
      allCycles.map((c) => [
        `#${c.cycleNumber}`,
        formatDateTime(c.startedAt),
        c.durationMs ? `${(c.durationMs / 1000).toFixed(1)}s` : '–',
        c.tiersRun.join(', '),
        String(c.orgsAttempted),
        String(c.orgsSucceeded),
        String(c.orgsFailed),
        String(c.apiCallsMade),
        c.trigger,
      ]),
      layout.startY,
      layout
    );
    downloadPdf(layout.doc, exportFilename('polling-cycles', 'pdf'));
  };

  // Actions
  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncHierarchy(activePlatformId);
      const s = result.summary;
      setSyncResult(
        `Synced: ${s.total} unique (${s.rawApiCount ?? '?'} raw from ${s.pagesFetched ?? '?'} pages), ` +
        `${s.new} new, ${s.updated} updated, ${s.dbCount ?? '?'} in DB`
      );
      await fetchData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setSyncResult(`Sync failed: ${msg}`);
    } finally {
      setSyncing(false);
    }
  }

  // Platform config handlers
  async function handleSavePlatform() {
    setSavingPlatform(true);
    try {
      await upsertPlatformConfig({
        ...platformForm,
        apiKey: platformForm.apiKey || undefined,
        auth0ConfigId: platformForm.auth0ConfigId || null,
      });
      setShowPlatformDialog(false);
      setPlatformForm({ label: '', slug: '', rootPortalUrl: '', portalUrlSuffix: '', apiKey: '', auth0ConfigId: '' });
      await fetchData();
    } catch {
      // Error handled by fetchApi
    } finally {
      setSavingPlatform(false);
    }
  }

  async function handleDeletePlatform(id: string) {
    try {
      await deletePlatformConfig(id);
      if (selectedPlatformId === id) {
        const prod = platformConfigs.find(c => c.isProduction);
        setSelectedPlatformId(prod?.id ?? null);
      }
      await fetchData();
    } catch {
      // Error handled by fetchApi
    } finally {
      setDeletingPlatformId(null);
    }
  }

  async function handleTogglePlatformPause(platform: PlatformConfig) {
    try {
      if (platform.pollingPaused) {
        await resumePlatformPolling(platform.id);
      } else {
        await pausePlatformPolling(platform.id);
      }
      await fetchData();
    } catch {
      // Error handled by fetchApi
    }
  }

  async function handleApprove(id: string, classification: string) {
    try {
      await approveOrganization(id, classification);
      await fetchData();
    } catch {
      // Error handling
    }
  }

  async function handleDeny(id: string) {
    try {
      await denyOrganization(id);
      await fetchData();
    } catch {
      // Error handling
    }
  }

  async function handleBulkApprove() {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    try {
      const promises = Array.from(selectedIds).map(id =>
        approveOrganization(id, bulkClassification)
      );
      await Promise.all(promises);
      await fetchData();
    } catch {
      // Error handling
    } finally {
      setBulkProcessing(false);
    }
  }

  async function handleBulkDeny() {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    try {
      const promises = Array.from(selectedIds).map(id => denyOrganization(id));
      await Promise.all(promises);
      await fetchData();
    } catch {
      // Error handling
    } finally {
      setBulkProcessing(false);
    }
  }

  const [auth0SaveSuccess, setAuth0SaveSuccess] = useState(false);

  async function handleSaveAuth0() {
    setSavingAuth0(true);
    setAuth0SaveSuccess(false);
    try {
      await upsertAuth0Config(
        auth0Form.label || auth0Form.domain,
        auth0Form.domain,
        auth0Form.clientId,
        auth0Form.clientSecret,
        auth0Form.isDefault
      );
      const configsRes = await getAuth0Configs();
      setAuth0Configs(configsRes.configs);
      // Clear form and show success
      setAuth0Form({ label: '', domain: '', clientId: '', clientSecret: '', isDefault: false });
      setShowSecret(false);
      setAuth0SaveSuccess(true);
      setTimeout(() => setAuth0SaveSuccess(false), 5000);
    } catch {
      // Error handling
    } finally {
      setSavingAuth0(false);
    }
  }

  async function handleDeleteAuth0(id: string) {
    try {
      await deleteAuth0Config(id);
      const configsRes = await getAuth0Configs();
      setAuth0Configs(configsRes.configs);
    } catch {
      // Error handling
    }
  }

  async function handlePollOrg(id: string, name: string) {
    setPollingOrgId(id);
    setPollResult(null);
    try {
      const result = await pollOrganization(id);
      const c = result.dataCounts;
      setPollResult(`${name}: ${c.nodes} nodes, ${c.neeveUsers} users, ${c.networks} networks`);
      await fetchData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Poll failed';
      setPollResult(`${name}: ${msg}`);
    } finally {
      setPollingOrgId(null);
    }
  }

  async function handleStatusToggle(org: Organization, newStatus: 'active' | 'disabled') {
    try {
      await updateOrgStatus(org.id, newStatus);
      if (newStatus === 'active') {
        // Show reactivation dialog asking about polling
        setReactivateOrg(org);
      }
      await fetchData();
    } catch {
      // Error handling
    }
  }

  async function handleReactivateResponse(enablePolling: boolean) {
    if (reactivateOrg && enablePolling) {
      try {
        await updateOrgPolling(reactivateOrg.id, true);
        await fetchData();
      } catch {
        // Error handling
      }
    }
    setReactivateOrg(null);
  }

  async function handleSavePollingConfig() {
    setSavingConfig(true);
    try {
      await updatePollingEngineConfig(configForm, activePlatformId);
      const status = await getPollingStatus(activePlatformId);
      setPollingStatus(status);
      setEditingConfig(false);
    } catch {
      // Error handling
    } finally {
      setSavingConfig(false);
    }
  }

  async function handleExportOrg(id: string) {
    try {
      await exportOrgData(id);
    } catch {
      // Error handling
    }
  }

  async function handleDecommission() {
    if (!decommissionOrg) return;
    try {
      await decommissionOrganization(decommissionOrg.id);
      setDecommissionOrg(null);
      await fetchData();
    } catch {
      // Error handling
    }
  }

  const [purgeOrg, setPurgeOrg] = useState<{ id: string; name: string } | null>(null);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [purging, setPurging] = useState(false);

  async function handlePurgeConfirm() {
    if (!purgeOrg) return;
    setPurging(true);
    try {
      await purgeOrgData(purgeOrg.id);
      setPurgeOrg(null);
      setPurgeConfirmText('');
      await fetchData();
    } catch {
      // Error handling
    } finally {
      setPurging(false);
    }
  }

  async function handleToggleEnginePause() {
    setTogglingPause(true);
    try {
      if (pollingStatus?.paused) {
        await resumePolling(activePlatformId);
      } else {
        const reason = window.prompt('Pause reason (optional):') ?? undefined;
        await pausePolling(reason, activePlatformId);
      }
      const status = await getPollingStatus(activePlatformId);
      setPollingStatus(status);
      const pcs = await getPlatformConfigs();
      setPlatformConfigs(pcs.configs);
    } catch {
      // Error handling
    } finally {
      setTogglingPause(false);
    }
  }

  async function handleToggleMasterPause() {
    setTogglingPause(true);
    try {
      // Master kill switch: always uses global endpoints (no platform ID)
      const isMasterPaused = activePlatformId ? pollingStatus?.masterPaused : pollingStatus?.paused;
      if (isMasterPaused) {
        await resumePolling(); // no platform ID = global
      } else {
        const reason = window.prompt('Master pause reason (optional):') ?? undefined;
        await pausePolling(reason); // no platform ID = global
      }
      const status = await getPollingStatus(activePlatformId);
      setPollingStatus(status);
    } catch {
      // Error handling
    } finally {
      setTogglingPause(false);
    }
  }

  // Derived data
  const selectedPlatform = platformConfigs.find(c => c.id === selectedPlatformId);
  const activeCount = organizations.filter(o => o.pollingEnabled).length;
  const pendingCount = pendingOrgs.length;

  const filteredOrgs = useMemo(() => {
    const dir = orgSortDir === 'asc' ? 1 : -1;
    return organizations
      .filter(org => {
        if (classificationFilter !== 'all' && org.classification !== classificationFilter) return false;
        if (orgStatusFilter !== 'all' && org.status !== orgStatusFilter) return false;
        if (orgAuthFilter !== 'all' && (orgAuthFilter === 'auth0') !== org.isCentralAuth) return false;
        if (orgPollingFilter !== 'all' && (orgPollingFilter === 'enabled') !== org.pollingEnabled) return false;
        if (search && !org.name.toLowerCase().includes(search.toLowerCase()) &&
          !(org.domainName || '').toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => {
        switch (orgSortBy) {
          case 'domain':
            return dir * (a.domainName ?? '').localeCompare(b.domainName ?? '');
          case 'status':
            return dir * a.status.localeCompare(b.status);
          case 'classification':
            return dir * a.classification.localeCompare(b.classification);
          case 'authType':
            return dir * (Number(a.isCentralAuth) - Number(b.isCentralAuth));
          case 'nodes':
            return dir * (a.nodeCount - b.nodeCount);
          case 'users':
            return dir * (a.userCount - b.userCount);
          case 'polling':
            return dir * (Number(a.pollingEnabled) - Number(b.pollingEnabled));
          case 'lastPoll': {
            const ta = a.lastSuccessfulPoll ? new Date(a.lastSuccessfulPoll).getTime() : 0;
            const tb = b.lastSuccessfulPoll ? new Date(b.lastSuccessfulPoll).getTime() : 0;
            return dir * (ta - tb);
          }
          default:
            return dir * a.name.localeCompare(b.name);
        }
      });
  }, [organizations, classificationFilter, orgStatusFilter, orgAuthFilter, orgPollingFilter, search, orgSortBy, orgSortDir]);

  const filteredPending = useMemo(() =>
    pendingOrgs
      .filter(org => {
        if (pendingClassFilter !== 'all' && org.classification !== pendingClassFilter) return false;
        if (pendingSearch && !org.name.toLowerCase().includes(pendingSearch.toLowerCase()) &&
          !(org.domainName || '').toLowerCase().includes(pendingSearch.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
    [pendingOrgs, pendingClassFilter, pendingSearch]
  );

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === filteredPending.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPending.map(o => o.id)));
    }
  }

  // Organizations table — sort, select, bulk actions, export
  function handleOrgSort(field: OrgSortField) {
    if (orgSortBy === field) {
      setOrgSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrgSortBy(field);
      setOrgSortDir('asc');
    }
  }

  const OrgSortIcon = ({ field }: { field: OrgSortField }) => {
    if (orgSortBy !== field)
      return <ChevronsUpDown className="dls-h-3.5 dls-w-3.5 dls-text-muted-foreground/50" />;
    return orgSortDir === 'asc' ? (
      <ArrowUp className="dls-h-3.5 dls-w-3.5" />
    ) : (
      <ArrowDown className="dls-h-3.5 dls-w-3.5" />
    );
  };

  const OrgSortableHeader = ({
    field,
    children,
    className = '',
  }: {
    field: OrgSortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => handleOrgSort(field)}
        className={`dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors dls-px-1 dls-py-0.5 dls-rounded ${className.includes('dls-text-right') ? 'dls-ml-auto' : '-dls-ml-1'}`}
      >
        {children}
        <OrgSortIcon field={field} />
      </button>
    </TableHead>
  );

  function toggleOrgSelect(id: string) {
    setOrgSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleOrgSelectAll() {
    if (orgSelectedIds.size === filteredOrgs.length) {
      setOrgSelectedIds(new Set());
    } else {
      setOrgSelectedIds(new Set(filteredOrgs.map(o => o.id)));
    }
  }

  const hasOrgFilters =
    classificationFilter !== 'all' || orgStatusFilter !== 'all' || orgAuthFilter !== 'all' || orgPollingFilter !== 'all';

  function clearOrgFilters() {
    setClassificationFilter('all');
    setOrgStatusFilter('all');
    setOrgAuthFilter('all');
    setOrgPollingFilter('all');
  }

  // Bulk mutations run in parallel (DB-only writes); per-org failures are
  // tolerated and summarized instead of aborting the batch.
  async function runOrgBulk(label: string, fn: (id: string) => Promise<unknown>) {
    const ids = Array.from(orgSelectedIds);
    if (ids.length === 0) return;
    setOrgBulkProcessing(true);
    setOrgBulkResult(null);
    const results = await Promise.allSettled(ids.map(fn));
    const failed = results.filter(r => r.status === 'rejected').length;
    setOrgBulkResult(`${label}: ${ids.length - failed} succeeded${failed ? `, ${failed} failed` : ''}`);
    await fetchData(activePlatformId);
    setOrgBulkProcessing(false);
  }

  const handleBulkOrgStatus = (status: 'active' | 'disabled') =>
    runOrgBulk(status === 'active' ? 'Activate' : 'Disable', id => updateOrgStatus(id, status));

  const handleBulkOrgClassification = () =>
    runOrgBulk(`Set ${orgBulkClassification}`, id => updateOrgClassification(id, orgBulkClassification));

  const handleBulkOrgPolling = (enabled: boolean) =>
    runOrgBulk(enabled ? 'Enable polling' : 'Disable polling', id => updateOrgPolling(id, enabled));

  // On-demand polls hit the external Neeve API — run sequentially so a bulk
  // selection doesn't burst past the per-portal rate limiter.
  async function handleBulkOrgPoll() {
    const ids = Array.from(orgSelectedIds);
    if (ids.length === 0) return;
    setOrgBulkProcessing(true);
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < ids.length; i++) {
      setOrgBulkResult(`Polling ${i + 1} of ${ids.length}...`);
      try {
        await pollOrganization(ids[i]);
        ok++;
      } catch {
        failed++;
      }
    }
    setOrgBulkResult(`On-demand poll: ${ok} succeeded${failed ? `, ${failed} failed` : ''}`);
    await fetchData(activePlatformId);
    setOrgBulkProcessing(false);
  }

  // Exports the table exactly as displayed — current filters AND sort applied
  function handleExportOrgsCsv() {
    if (!filteredOrgs.length) return;
    downloadCsv(
      filteredOrgs.map(o => ({
        Name: o.name,
        Domain: o.domainName || '',
        Status: o.status,
        Classification: o.classification,
        'Auth Type': o.isCentralAuth ? 'Auth0' : 'Legacy',
        Nodes: o.nodeCount,
        Users: o.userCount,
        Polling: o.pollingEnabled ? 'enabled' : 'disabled',
        'Last Successful Poll': formatExportDate(o.lastSuccessfulPoll),
        'Last Poll Error': o.lastPollError || '',
        'Consecutive Failures': o.consecutivePollFailures ?? 0,
        Discovered: formatExportDate(o.discoveredAt),
      })),
      exportFilename('organizations', 'csv'),
      { report: 'Organizations', title: 'Neeve Insights — Dev Console' }
    );
  }

  // Poll health: active orgs with a recent successful poll and no errors
  const polledHealthy = organizations.filter(o =>
    o.pollingEnabled && o.lastSuccessfulPoll && !o.lastPollError && (o.consecutivePollFailures ?? 0) === 0
  ).length;

  const kpiItems: KpiItem[] = [
    { label: 'Total Organizations', value: organizations.length },
    { label: 'Active (Polling)', value: activeCount, variant: 'success' },
    { label: 'Poll Health', value: `${polledHealthy}/${activeCount}`, variant: polledHealthy === activeCount ? 'success' : 'warning', tooltip: 'Healthy polls / active orgs (no errors, no circuit breakers)' },
    { label: 'Pending Approval', value: pendingCount, variant: pendingCount > 0 ? 'warning' : 'default' },
  ];

  if (loading) {
    return (
      <div className="dls-py-6 dls-pb-8">
        <div className="dls-container dls-mx-auto dls-px-4 dls-max-w-7xl">
          <div className="dls-flex dls-items-center dls-justify-center dls-py-24 dls-text-muted-foreground">
            <RefreshCw className="dls-h-5 dls-w-5 dls-animate-spin dls-mr-2" />
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dls-py-6 dls-pb-8">
      <div className="dls-container dls-mx-auto dls-px-4 dls-max-w-7xl dls-space-y-6">
        {/* Master Kill Switch Banner — blocks ALL platforms */}
        {(activePlatformId ? pollingStatus?.masterPaused : pollingStatus?.paused) && (
          <div className="dls-bg-status-offline/10 dls-border dls-border-status-offline/30 dls-rounded-lg dls-px-4 dls-py-3 dls-flex dls-items-center dls-justify-between">
            <div className="dls-flex dls-items-center dls-gap-2">
              <Shield className="dls-h-4 dls-w-4 dls-text-status-offline" />
              <span className="dls-text-sm dls-font-medium dls-text-status-offline">
                MASTER KILL SWITCH ACTIVE — all polling stopped across all platforms
              </span>
            </div>
            <Button size="sm" variant="destructive" onClick={handleToggleMasterPause} disabled={togglingPause}>
              {togglingPause ? 'Resuming...' : 'Resume All'}
            </Button>
          </div>
        )}
        {/* Per-Platform Paused Banner — only when master is NOT the cause */}
        {activePlatformId && pollingStatus?.paused && !pollingStatus?.masterPaused && (
          <div className="dls-bg-status-warning/10 dls-border dls-border-status-warning/30 dls-rounded-lg dls-px-4 dls-py-3 dls-flex dls-items-center dls-justify-between">
            <div className="dls-flex dls-items-center dls-gap-2">
              <Clock className="dls-h-4 dls-w-4 dls-text-status-warning" />
              <span className="dls-text-sm dls-font-medium dls-text-status-warning">
                {selectedPlatform?.label ?? 'Platform'} polling is PAUSED
                {pollingStatus.pauseReason && ` — ${pollingStatus.pauseReason}`}
              </span>
            </div>
            <Button size="sm" onClick={handleToggleEnginePause} disabled={togglingPause}>
              {togglingPause ? 'Resuming...' : 'Resume Polling'}
            </Button>
          </div>
        )}

        {/* Page Header */}
        <div>
          <h1 className="dls-text-2xl dls-font-bold dls-text-foreground">Dev Console</h1>
          <p className="dls-text-sm dls-text-muted-foreground dls-mt-1">
            Multi-tenant organization management
          </p>
        </div>

        {/* Developer Preferences — local-only per-user toggles. Never flag-gated. */}
        <DeveloperPreferencesCard />

        {/* Per-org feature override admin (server-side, audited). */}
        <FeatureOverrideAdmin organizations={organizations} />


        {/* Platform Selector */}
        {platformConfigs.length > 1 && (
          <div className="dls-flex dls-items-center dls-gap-2">
            {platformConfigs.map((pc) => (
              <button
                key={pc.id}
                onClick={() => setSelectedPlatformId(pc.id)}
                className={`dls-px-4 dls-py-2 dls-text-sm dls-font-medium dls-rounded-lg dls-border dls-transition-colors ${
                  selectedPlatformId === pc.id
                    ? pc.isProduction
                      ? 'dls-bg-status-online/10 dls-border-status-online/30 dls-text-status-online'
                      : 'dls-bg-amber-50 dls-border-amber-300 dls-text-amber-700'
                    : 'dls-bg-white dls-border-border dls-text-muted-foreground hover:dls-bg-muted/50'
                }`}
              >
                <span className="dls-flex dls-items-center dls-gap-2">
                  <span className={`dls-w-2 dls-h-2 dls-rounded-full ${pc.isProduction ? 'dls-bg-status-online' : 'dls-bg-amber-500'}`} />
                  {pc.label}
                  <Badge variant="secondary" className="dls-text-[10px] dls-px-1.5 dls-py-0">
                    {pc.orgCount}
                  </Badge>
                </span>
              </button>
            ))}
          </div>
        )}

        {/* KPI Strip */}
        <KpiStrip items={kpiItems} />

        {/* 1. Auth0 Configurations Card — multi-environment */}
        <Card>
          <CardHeader>
            <CardTitle className="dls-flex dls-items-center dls-gap-2">
              <Shield className="dls-h-5 dls-w-5" />
              Auth0 Configurations
              <Badge variant="secondary" className="dls-ml-2">
                {auth0Configs.length} {auth0Configs.length === 1 ? 'environment' : 'environments'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="dls-space-y-4">
              {/* Existing configs */}
              {auth0Configs.length > 0 && (
                <div className="dls-space-y-2 dls-pb-4 dls-border-b dls-border-border">
                  {auth0Configs.map((cfg) => (
                    <div key={cfg.id} className="dls-flex dls-items-center dls-justify-between dls-rounded-md dls-border dls-border-border dls-px-3 dls-py-2">
                      <div className="dls-flex dls-items-center dls-gap-3">
                        <div>
                          <div className="dls-flex dls-items-center dls-gap-2">
                            <span className="dls-text-sm dls-font-semibold">{cfg.label}</span>
                            {cfg.isDefault && (
                              <Badge variant="secondary" className="dls-bg-status-online/10 dls-text-status-online dls-border-status-online/20 dls-text-[10px] dls-px-1.5 dls-py-0">
                                Default
                              </Badge>
                            )}
                          </div>
                          <div className="dls-text-xs dls-text-muted-foreground dls-font-mono">{cfg.domain}</div>
                        </div>
                      </div>
                      <div className="dls-flex dls-items-center dls-gap-3">
                        <span className="dls-text-xs dls-text-muted-foreground">{cfg.orgCount} orgs</span>
                        <span className="dls-text-xs dls-text-muted-foreground dls-font-mono">{cfg.clientId}</span>
                        <Button variant="ghost" size="sm" className="dls-h-7 dls-w-7 dls-p-0 dls-text-muted-foreground hover:dls-text-status-offline" onClick={() => handleDeleteAuth0(cfg.id)}>
                          <Trash2 className="dls-h-3.5 dls-w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Add / Update form */}
              <div className="dls-space-y-3">
                <h4 className="dls-text-sm dls-font-semibold">Add Auth0 Environment</h4>
                <div className="dls-grid dls-grid-cols-1 sm:dls-grid-cols-2 lg:dls-grid-cols-4 dls-gap-3">
                  <div>
                    <label className="dls-text-xs dls-font-medium dls-text-muted-foreground">Label</label>
                    <Input placeholder="e.g. Production, Staging" value={auth0Form.label} onChange={e => setAuth0Form(prev => ({ ...prev, label: e.target.value }))} className="dls-mt-1" />
                  </div>
                  <div>
                    <label className="dls-text-xs dls-font-medium dls-text-muted-foreground">Domain</label>
                    <Input placeholder="id.neeve.ai" value={auth0Form.domain} onChange={e => setAuth0Form(prev => ({ ...prev, domain: e.target.value }))} className="dls-mt-1" />
                  </div>
                  <div>
                    <label className="dls-text-xs dls-font-medium dls-text-muted-foreground">Client ID</label>
                    <Input placeholder="Client ID" value={auth0Form.clientId} onChange={e => setAuth0Form(prev => ({ ...prev, clientId: e.target.value }))} className="dls-mt-1" />
                  </div>
                  <div>
                    <label className="dls-text-xs dls-font-medium dls-text-muted-foreground">Client Secret</label>
                    <div className="dls-relative dls-mt-1">
                      <Input type={showSecret ? 'text' : 'password'} placeholder="Client Secret" value={auth0Form.clientSecret} onChange={e => setAuth0Form(prev => ({ ...prev, clientSecret: e.target.value }))} className="dls-pr-10" />
                      <button type="button" onClick={() => setShowSecret(!showSecret)} className="dls-absolute dls-right-2.5 dls-top-1/2 -dls-translate-y-1/2 dls-text-muted-foreground hover:dls-text-foreground">
                        {showSecret ? <EyeOff className="dls-h-4 dls-w-4" /> : <Eye className="dls-h-4 dls-w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="dls-flex dls-items-center dls-gap-3">
                  <label className="dls-flex dls-items-center dls-gap-1.5 dls-text-xs dls-text-muted-foreground dls-cursor-pointer">
                    <input type="checkbox" checked={auth0Form.isDefault} onChange={e => setAuth0Form(prev => ({ ...prev, isDefault: e.target.checked }))} className="dls-rounded" />
                    Set as default
                  </label>
                </div>
                <div className="dls-flex dls-items-center dls-gap-3">
                  <Button onClick={handleSaveAuth0} disabled={savingAuth0 || !auth0Form.domain || !auth0Form.clientId || !auth0Form.clientSecret} size="sm">
                    {savingAuth0 ? 'Saving...' : 'Save Auth0 Config'}
                  </Button>
                  {auth0SaveSuccess && (
                    <span className="dls-flex dls-items-center dls-gap-1.5 dls-text-sm dls-text-status-online dls-animate-in dls-fade-in">
                      <CheckCircle className="dls-h-4 dls-w-4" />
                      Configuration saved
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 1b. Platform Environments Card */}
        <Card>
          <CardHeader>
            <CardTitle className="dls-flex dls-items-center dls-gap-2">
              <Globe className="dls-h-5 dls-w-5" />
              Platform Environments
              <Badge variant="secondary" className="dls-ml-2">
                {platformConfigs.length} {platformConfigs.length === 1 ? 'platform' : 'platforms'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="dls-space-y-4">
              {/* Platform list */}
              {platformConfigs.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Root URL</TableHead>
                      <TableHead>Suffix</TableHead>
                      <TableHead>Auth0</TableHead>
                      <TableHead>Orgs</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Sync</TableHead>
                      <TableHead className="dls-w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {platformConfigs.map((pc) => (
                      <TableRow key={pc.id}>
                        <TableCell>
                          <div className="dls-flex dls-items-center dls-gap-2">
                            <span className="dls-font-medium">{pc.label}</span>
                            <Badge
                              variant="secondary"
                              className={pc.isProduction
                                ? 'dls-bg-status-online/10 dls-text-status-online dls-border-status-online/20 dls-text-[10px] dls-px-1.5 dls-py-0'
                                : 'dls-bg-amber-50 dls-text-amber-700 dls-border-amber-200 dls-text-[10px] dls-px-1.5 dls-py-0'
                              }
                            >
                              {pc.isProduction ? 'PROD' : 'STG'}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="dls-text-xs dls-font-mono dls-text-muted-foreground">{pc.rootPortalUrl}</TableCell>
                        <TableCell className="dls-text-xs dls-font-mono dls-text-muted-foreground">{pc.portalUrlSuffix}</TableCell>
                        <TableCell className="dls-text-xs dls-text-muted-foreground">
                          {pc.auth0ConfigId
                            ? auth0Configs.find(a => a.id === pc.auth0ConfigId)?.label ?? '–'
                            : '–'}
                        </TableCell>
                        <TableCell>{pc.orgCount}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleTogglePlatformPause(pc)}
                            className="dls-cursor-pointer"
                          >
                            <Badge variant="secondary" className={pc.pollingPaused
                              ? 'dls-bg-status-warning/10 dls-text-status-warning dls-border-status-warning/20'
                              : 'dls-bg-status-online/10 dls-text-status-online dls-border-status-online/20'
                            }>
                              {pc.pollingPaused ? 'Paused' : 'Active'}
                            </Badge>
                          </button>
                        </TableCell>
                        <TableCell className="dls-text-xs dls-text-muted-foreground">
                          {pc.lastHierarchySyncAt ? formatDateTime(pc.lastHierarchySyncAt) : 'Never'}
                        </TableCell>
                        <TableCell>
                          <div className="dls-flex dls-gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setPlatformForm({
                                  label: pc.label,
                                  slug: pc.slug,
                                  rootPortalUrl: pc.rootPortalUrl,
                                  portalUrlSuffix: pc.portalUrlSuffix,
                                  apiKey: '',
                                  auth0ConfigId: pc.auth0ConfigId ?? '',
                                });
                                setShowPlatformDialog(true);
                              }}
                              className="dls-h-7 dls-w-7 dls-p-0 dls-text-muted-foreground hover:dls-text-foreground"
                              title="Edit platform"
                            >
                              <Settings className="dls-h-3.5 dls-w-3.5" />
                            </Button>
                            {!pc.isProduction && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeletingPlatformId(pc.id)}
                                className="dls-h-7 dls-w-7 dls-p-0 dls-text-status-offline hover:dls-text-status-offline"
                              >
                                <Trash2 className="dls-h-3.5 dls-w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Add Platform form */}
              <div className="dls-border-t dls-border-border dls-pt-4">
                <Button size="sm" variant="secondary" onClick={() => setShowPlatformDialog(true)}>
                  Add Platform
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Platform Dialog */}
        <Dialog open={showPlatformDialog} onOpenChange={setShowPlatformDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Platform Environment</DialogTitle>
              <DialogDescription>
                Add a new Neeve Secure Edge platform (e.g., Staging) to validate polling against.
              </DialogDescription>
            </DialogHeader>
            <div className="dls-space-y-3">
              <div>
                <label className="dls-text-sm dls-font-medium">Label</label>
                <Input
                  placeholder="Staging"
                  value={platformForm.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    setPlatformForm(f => ({
                      ...f,
                      label,
                      slug: label.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'),
                    }));
                  }}
                />
              </div>
              <div>
                <label className="dls-text-sm dls-font-medium">Root Portal URL</label>
                <Input
                  placeholder="https://root.staging.secureedge.neeve.ai"
                  value={platformForm.rootPortalUrl}
                  onChange={(e) => setPlatformForm(f => ({ ...f, rootPortalUrl: e.target.value }))}
                />
              </div>
              <div>
                <label className="dls-text-sm dls-font-medium">Portal URL Suffix</label>
                <Input
                  placeholder=".staging.secureedge.neeve.ai"
                  value={platformForm.portalUrlSuffix}
                  onChange={(e) => setPlatformForm(f => ({ ...f, portalUrlSuffix: e.target.value }))}
                />
              </div>
              <div>
                <label className="dls-text-sm dls-font-medium">API Key</label>
                <div className="dls-relative">
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    placeholder="Root API key for this platform"
                    value={platformForm.apiKey}
                    onChange={(e) => setPlatformForm(f => ({ ...f, apiKey: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="dls-absolute dls-right-2 dls-top-2 dls-text-muted-foreground"
                  >
                    {showSecret ? <EyeOff className="dls-h-4 dls-w-4" /> : <Eye className="dls-h-4 dls-w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="dls-text-sm dls-font-medium">Auth0 Environment</label>
                <Select
                  value={platformForm.auth0ConfigId || 'none'}
                  onValueChange={(v) => setPlatformForm(f => ({ ...f, auth0ConfigId: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="dls-mt-1">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {auth0Configs.map((cfg) => (
                      <SelectItem key={cfg.id} value={cfg.id}>
                        {cfg.label} ({cfg.domain})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="dls-text-xs dls-text-muted-foreground dls-mt-1">
                  Orgs discovered from this platform will be auto-linked to this Auth0 config
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setShowPlatformDialog(false)}>Cancel</Button>
              <Button onClick={handleSavePlatform} disabled={savingPlatform || !platformForm.label || !platformForm.rootPortalUrl || !platformForm.portalUrlSuffix}>
                {savingPlatform ? 'Saving...' : 'Save Platform'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Platform Confirmation */}
        <Dialog open={!!deletingPlatformId} onOpenChange={() => setDeletingPlatformId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Platform</DialogTitle>
              <DialogDescription>
                This will permanently delete all organizations discovered from this platform and their associated data.
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setDeletingPlatformId(null)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => deletingPlatformId && handleDeletePlatform(deletingPlatformId)}
              >
                Delete Platform
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 2. Platform Configuration & Polling Controls Card */}
        <Card>
          <CardHeader>
            <CardTitle className="dls-flex dls-items-center dls-gap-2">
              <Settings className="dls-h-5 dls-w-5" />
              {selectedPlatform ? `${selectedPlatform.label} Configuration` : 'Root Configuration'}
              {selectedPlatform && (
                <Badge
                  variant="secondary"
                  className={selectedPlatform.isProduction
                    ? 'dls-bg-status-online/10 dls-text-status-online dls-border-status-online/20 dls-text-[10px] dls-px-1.5 dls-py-0'
                    : 'dls-bg-amber-50 dls-text-amber-700 dls-border-amber-200 dls-text-[10px] dls-px-1.5 dls-py-0'
                  }
                >
                  {selectedPlatform.isProduction ? 'PROD' : 'STG'}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="dls-space-y-4">
              {/* Platform-aware configuration display */}
              {selectedPlatform ? (
                selectedPlatform.hasApiKey || (selectedPlatform.isProduction && rootConfig?.isConfigured) ? (
                  <>
                    <div className="dls-grid dls-grid-cols-1 sm:dls-grid-cols-3 dls-gap-4">
                      <div>
                        <div className="dls-text-xs dls-font-semibold dls-text-muted-foreground dls-mb-1">Root Portal URL</div>
                        <div className="dls-text-sm dls-font-mono">{selectedPlatform.rootPortalUrl}</div>
                      </div>
                      <div>
                        <div className="dls-text-xs dls-font-semibold dls-text-muted-foreground dls-mb-1">Portal URL Suffix</div>
                        <div className="dls-text-sm dls-font-mono">{selectedPlatform.portalUrlSuffix}</div>
                      </div>
                      <div>
                        <div className="dls-text-xs dls-font-semibold dls-text-muted-foreground dls-mb-1">Last Hierarchy Sync</div>
                        <div className="dls-text-sm">{selectedPlatform.lastHierarchySyncAt ? formatDateTime(selectedPlatform.lastHierarchySyncAt) : 'Never'}</div>
                      </div>
                    </div>
                    <div className="dls-flex dls-items-center dls-gap-3">
                      <Button onClick={handleSync} disabled={syncing} size="sm">
                        <RefreshCw className={`dls-h-4 dls-w-4 dls-mr-2 ${syncing ? 'dls-animate-spin' : ''}`} />
                        {syncing ? 'Syncing...' : 'Sync Hierarchy'}
                      </Button>
                      <Badge variant="secondary" className={selectedPlatform.hasApiKey
                        ? 'dls-bg-status-online/10 dls-text-status-online dls-border-status-online/20'
                        : 'dls-bg-status-warning/10 dls-text-status-warning dls-border-status-warning/20'
                      }>
                        {selectedPlatform.hasApiKey ? 'API Key Configured' : selectedPlatform.isProduction ? 'Using ENV Key' : 'No API Key'}
                      </Badge>
                      {syncResult && <span className="dls-text-sm dls-text-muted-foreground">{syncResult}</span>}
                    </div>
                  </>
                ) : (
                  <div className="dls-flex dls-items-center dls-gap-2 dls-text-muted-foreground">
                    <XCircle className="dls-h-4 dls-w-4 dls-text-status-offline" />
                    <span className="dls-text-sm">
                      {selectedPlatform.isProduction
                        ? 'ROOT_API_KEY environment variable not set. Add it in Vercel project settings or configure an API key in Platform Environments above.'
                        : `No API key configured for ${selectedPlatform.label}. Edit the platform in Platform Environments above to add one.`}
                    </span>
                  </div>
                )
              ) : rootConfig?.isConfigured ? (
                <>
                  <div className="dls-grid dls-grid-cols-1 sm:dls-grid-cols-3 dls-gap-4">
                    <div>
                      <div className="dls-text-xs dls-font-semibold dls-text-muted-foreground dls-mb-1">Portal URL</div>
                      <div className="dls-text-sm dls-font-mono">{rootConfig.portalUrl || '–'}</div>
                    </div>
                    <div>
                      <div className="dls-text-xs dls-font-semibold dls-text-muted-foreground dls-mb-1">Polling Interval</div>
                      <div className="dls-text-sm">{rootConfig.pollingIntervalSeconds ? `${rootConfig.pollingIntervalSeconds}s` : '–'}</div>
                    </div>
                    <div>
                      <div className="dls-text-xs dls-font-semibold dls-text-muted-foreground dls-mb-1">Last Hierarchy Sync</div>
                      <div className="dls-text-sm">{rootConfig.lastHierarchySyncAt ? formatDateTime(rootConfig.lastHierarchySyncAt) : 'Never'}</div>
                    </div>
                  </div>
                  <div className="dls-flex dls-items-center dls-gap-3">
                    <Button onClick={handleSync} disabled={syncing} size="sm">
                      <RefreshCw className={`dls-h-4 dls-w-4 dls-mr-2 ${syncing ? 'dls-animate-spin' : ''}`} />
                      {syncing ? 'Syncing...' : 'Sync Now'}
                    </Button>
                    <Badge variant={rootConfig.isActive ? 'default' : 'secondary'}>{rootConfig.isActive ? 'Active' : 'Inactive'}</Badge>
                    {syncResult && <span className="dls-text-sm dls-text-muted-foreground">{syncResult}</span>}
                  </div>
                </>
              ) : (
                <div className="dls-flex dls-items-center dls-gap-2 dls-text-muted-foreground">
                  <XCircle className="dls-h-4 dls-w-4 dls-text-status-offline" />
                  <span className="dls-text-sm">ROOT_API_KEY environment variable not set. Add it in Vercel project settings to enable org discovery.</span>
                </div>
              )}
              {/* Master Kill Switch — stops ALL platforms */}
              <div className="dls-border-t dls-border-border dls-pt-4">
                <div className="dls-flex dls-items-center dls-justify-between">
                  <div>
                    <div className="dls-text-sm dls-font-semibold dls-flex dls-items-center dls-gap-1.5">
                      <Shield className="dls-h-3.5 dls-w-3.5" />
                      Master Kill Switch
                    </div>
                    <div className="dls-text-xs dls-text-muted-foreground">
                      Emergency stop — pauses all polling across all platforms
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={(activePlatformId ? pollingStatus?.masterPaused : pollingStatus?.paused) ? 'default' : 'destructive'}
                    onClick={handleToggleMasterPause}
                    disabled={togglingPause}
                  >
                    {togglingPause ? 'Working...' : (activePlatformId ? pollingStatus?.masterPaused : pollingStatus?.paused) ? 'Resume All' : 'PAUSE ALL'}
                  </Button>
                </div>
              </div>
              {/* Per-Platform Polling Engine — scoped to selected platform */}
              <div className="dls-border-t dls-border-border dls-pt-4">
                <div className="dls-flex dls-items-center dls-justify-between">
                  <div>
                    <div className="dls-text-sm dls-font-semibold">
                      {selectedPlatform ? `${selectedPlatform.label} Polling Engine` : 'Organization Polling Engine'}
                    </div>
                    <div className="dls-text-xs dls-text-muted-foreground">
                      Cycle #{pollingStatus?.pollCycleCounter ?? 0}
                      {pollingStatus?.paused && ` — paused${pollingStatus.pausedBy ? ` by ${pollingStatus.pausedBy}` : ''}`}
                      {(activePlatformId ? pollingStatus?.masterPaused : pollingStatus?.paused) && !pollingStatus?.paused && ' — blocked by master kill switch'}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={pollingStatus?.paused ? 'default' : 'destructive'}
                    onClick={handleToggleEnginePause}
                    disabled={togglingPause || !!(activePlatformId ? pollingStatus?.masterPaused : false)}
                    title={(activePlatformId && pollingStatus?.masterPaused) ? 'Master kill switch is active — resume it first' : undefined}
                  >
                    {togglingPause ? 'Working...' : pollingStatus?.paused ? 'Resume Polling' : 'Pause Polling'}
                  </Button>
                </div>
                {pollingStatus?.openCircuitBreakers && pollingStatus.openCircuitBreakers.length > 0 && (
                  <div className="dls-mt-3">
                    <div className="dls-text-xs dls-font-semibold dls-text-muted-foreground dls-mb-1">
                      Open Circuit Breakers ({pollingStatus.openCircuitBreakers.length})
                    </div>
                    <div className="dls-flex dls-flex-wrap dls-gap-1">
                      {pollingStatus.openCircuitBreakers.map(cb => (
                        <Badge key={cb.id} variant="secondary" className="dls-text-xs dls-bg-status-offline/10 dls-text-status-offline">
                          {cb.name} ({cb.consecutivePollFailures} failures)
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Polling Engine Configuration — always visible */}
              <div className="dls-border-t dls-border-border dls-pt-4">
                <div className="dls-flex dls-items-center dls-justify-between dls-mb-3">
                  <div className="dls-text-sm dls-font-semibold">Engine Configuration</div>
                  {!editingConfig ? (
                    <Button size="sm" variant="secondary" onClick={() => {
                      const pc = (pollingStatus?.pollingConfig ?? {}) as Record<string, number | boolean>;
                      setConfigForm({
                        orgBatchSize: (pc.orgBatchSize as number) ?? 5,
                        perPortalRateLimit: (pc.perPortalRateLimit as number) ?? 120,
                        perPortalBurstSize: (pc.perPortalBurstSize as number) ?? 20,
                        maxCycleDurationSec: (pc.maxCycleDurationSec as number) ?? 750,
                        statsEnabled: (pc.statsEnabled as boolean) ?? true,
                        statsBatchSize: (pc.statsBatchSize as number) ?? 10,
                      });
                      setEditingConfig(true);
                    }}>
                      <Settings className="dls-h-3.5 dls-w-3.5 dls-mr-1.5" />
                      Edit
                    </Button>
                  ) : (
                    <div className="dls-flex dls-gap-2">
                      <Button size="sm" onClick={handleSavePollingConfig} disabled={savingConfig}>
                        {savingConfig ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditingConfig(false)}>Cancel</Button>
                    </div>
                  )}
                </div>
                {editingConfig ? (
                  <div className="dls-grid dls-grid-cols-2 sm:dls-grid-cols-3 dls-gap-3">
                    <div>
                      <label className="dls-text-xs dls-text-muted-foreground">Org Batch Size</label>
                      <Input type="number" min={1} max={50} value={configForm.orgBatchSize} onChange={e => setConfigForm(f => ({ ...f, orgBatchSize: Number(e.target.value) }))} className="dls-h-8 dls-text-sm" />
                    </div>
                    <div>
                      <label className="dls-text-xs dls-text-muted-foreground">Rate Limit (req/min/portal)</label>
                      <Input type="number" min={1} max={600} value={configForm.perPortalRateLimit} onChange={e => setConfigForm(f => ({ ...f, perPortalRateLimit: Number(e.target.value) }))} className="dls-h-8 dls-text-sm" />
                    </div>
                    <div>
                      <label className="dls-text-xs dls-text-muted-foreground">Burst Size</label>
                      <Input type="number" min={1} max={100} value={configForm.perPortalBurstSize} onChange={e => setConfigForm(f => ({ ...f, perPortalBurstSize: Number(e.target.value) }))} className="dls-h-8 dls-text-sm" />
                    </div>
                    <div>
                      <label className="dls-text-xs dls-text-muted-foreground">Max Cycle Duration (s)</label>
                      <Input type="number" min={30} max={780} value={configForm.maxCycleDurationSec} onChange={e => setConfigForm(f => ({ ...f, maxCycleDurationSec: Number(e.target.value) }))} className="dls-h-8 dls-text-sm" />
                    </div>
                    <div>
                      <label className="dls-text-xs dls-text-muted-foreground">Stats Batch Size</label>
                      <Input type="number" min={1} max={50} value={configForm.statsBatchSize} onChange={e => setConfigForm(f => ({ ...f, statsBatchSize: Number(e.target.value) }))} className="dls-h-8 dls-text-sm" />
                    </div>
                    <div className="dls-flex dls-items-end dls-gap-2 dls-pb-1">
                      <label className="dls-text-xs dls-text-muted-foreground">Stats Polling</label>
                      <button onClick={() => setConfigForm(f => ({ ...f, statsEnabled: !f.statsEnabled }))} className="dls-text-sm">
                        {configForm.statsEnabled ? <CheckCircle className="dls-h-4 dls-w-4 dls-text-status-online" /> : <XCircle className="dls-h-4 dls-w-4 dls-text-muted-foreground" />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="dls-grid dls-grid-cols-2 sm:dls-grid-cols-3 dls-gap-x-6 dls-gap-y-1 dls-text-xs">
                    {(() => {
                      const pc = (pollingStatus?.pollingConfig ?? {}) as Record<string, number | boolean>;
                      return (<>
                        <div><span className="dls-text-muted-foreground">Batch Size:</span> {pc.orgBatchSize ?? 5}</div>
                        <div><span className="dls-text-muted-foreground">Rate Limit:</span> {pc.perPortalRateLimit ?? 60}/min</div>
                        <div><span className="dls-text-muted-foreground">Burst:</span> {pc.perPortalBurstSize ?? 10}</div>
                        <div><span className="dls-text-muted-foreground">Max Duration:</span> {pc.maxCycleDurationSec ?? 270}s</div>
                        <div><span className="dls-text-muted-foreground">Stats Batch:</span> {pc.statsBatchSize ?? 10}</div>
                        <div><span className="dls-text-muted-foreground">Stats:</span> {(pc.statsEnabled ?? true) ? 'Enabled' : 'Disabled'}</div>
                      </>);
                    })()}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Pending Approvals — bounded height */}
        {pendingOrgs.length > 0 && (
          <Card>
            <CardHeader>
              <div className="dls-flex dls-flex-col sm:dls-flex-row dls-items-start sm:dls-items-center dls-justify-between dls-gap-4">
                <CardTitle className="dls-flex dls-items-center dls-gap-2">
                  <Clock className="dls-h-5 dls-w-5 dls-text-status-warning" />
                  Pending Approvals
                  <span className="dls-text-sm dls-font-normal dls-text-muted-foreground">
                    ({filteredPending.length} of {pendingOrgs.length})
                  </span>
                </CardTitle>
                <div className="dls-flex dls-items-center dls-gap-3">
                  <div className="dls-relative">
                    <Search className="dls-absolute dls-left-2.5 dls-top-1/2 -dls-translate-y-1/2 dls-h-3.5 dls-w-3.5 dls-text-muted-foreground" />
                    <Input placeholder="Search pending..." value={pendingSearch} onChange={e => setPendingSearch(e.target.value)} className="dls-w-48 dls-pl-8 dls-h-9 dls-rounded-xl" />
                  </div>
                  <Select value={pendingClassFilter} onValueChange={(v) => setPendingClassFilter(v as ClassificationFilter)}>
                    <SelectTrigger className="dls-w-36 dls-h-9"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="internal">Internal</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="dls-flex dls-items-center dls-gap-3 dls-mb-4 dls-pb-4 dls-border-b dls-border-border">
                <span className="dls-text-sm dls-text-muted-foreground">{selectedIds.size > 0 ? `${selectedIds.size} selected` : `${filteredPending.length} shown`}</span>
                <Select value={bulkClassification} onValueChange={setBulkClassification}>
                  <SelectTrigger className="dls-w-32 dls-h-8 dls-text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="shared">Shared</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="demo">Demo</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" disabled={selectedIds.size === 0 || bulkProcessing} onClick={handleBulkApprove}>
                  <CheckCircle className="dls-h-3.5 dls-w-3.5 dls-mr-1" />
                  {bulkProcessing ? 'Processing...' : `Approve ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
                </Button>
                <Button size="sm" variant="destructive" disabled={selectedIds.size === 0 || bulkProcessing} onClick={handleBulkDeny}>
                  <XCircle className="dls-h-3.5 dls-w-3.5 dls-mr-1" />
                  Deny {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
                </Button>
              </div>
              <div className="dls-max-h-[500px] dls-overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="dls-w-10">
                        <input type="checkbox" checked={filteredPending.length > 0 && selectedIds.size === filteredPending.length} onChange={toggleSelectAll} className="dls-rounded dls-border-border" />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>Auto-Classification</TableHead>
                      <TableHead>Auth Type</TableHead>
                      <TableHead className="dls-text-right">Nodes</TableHead>
                      <TableHead className="dls-text-right">Users</TableHead>
                      <TableHead className="dls-text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPending.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="dls-text-center dls-py-12 dls-text-muted-foreground">No pending organizations match your filter</TableCell></TableRow>
                    ) : (
                      filteredPending.map(org => (
                        <TableRow key={org.id}>
                          <TableCell><input type="checkbox" checked={selectedIds.has(org.id)} onChange={() => toggleSelect(org.id)} className="dls-rounded dls-border-border" /></TableCell>
                          <TableCell className="dls-font-medium">{org.name}</TableCell>
                          <TableCell className="dls-text-muted-foreground dls-text-sm">{org.domainName || '–'}</TableCell>
                          <TableCell><Badge variant="secondary" className={CLASSIFICATION_BADGE_CLASSES[org.classification]}>{org.classification}</Badge></TableCell>
                          <TableCell className="dls-text-sm">{org.isCentralAuth ? 'Auth0' : 'Legacy'}</TableCell>
                          <TableCell className="dls-text-right dls-font-mono dls-text-sm">{org.nodeCount}</TableCell>
                          <TableCell className="dls-text-right dls-font-mono dls-text-sm">{org.userCount}</TableCell>
                          <TableCell className="dls-text-right">
                            <div className="dls-flex dls-justify-end dls-gap-1">
                              <Button size="sm" variant="ghost" className="dls-h-7 dls-px-2 dls-text-xs" onClick={() => handleApprove(org.id, 'customer')}>
                                <CheckCircle className="dls-h-3.5 dls-w-3.5 dls-mr-1 dls-text-status-online" /> Approve
                              </Button>
                              <Button size="sm" variant="ghost" className="dls-h-7 dls-px-2 dls-text-xs dls-text-status-offline" onClick={() => handleDeny(org.id)}>
                                <XCircle className="dls-h-3.5 dls-w-3.5 dls-mr-1" /> Deny
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 4. Organizations Table — bounded height, status toggle, polling guard */}
        <Card>
          <CardHeader>
            <div className="dls-flex dls-flex-col sm:dls-flex-row dls-items-start sm:dls-items-center dls-justify-between dls-gap-4">
              <CardTitle className="dls-flex dls-items-center dls-gap-2">
                <Globe className="dls-h-5 dls-w-5" />
                Organizations
                <span className="dls-text-sm dls-font-normal dls-text-muted-foreground">
                  ({filteredOrgs.length} of {organizations.length})
                </span>
              </CardTitle>
              <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap">
                {orgStatusFilter !== 'all' && (
                  <FilterChip
                    label={`Status: ${orgStatusFilter.replace('_', ' ')}`}
                    onRemove={() => setOrgStatusFilter('all')}
                  />
                )}
                {classificationFilter !== 'all' && (
                  <FilterChip
                    label={`Classification: ${classificationFilter}`}
                    onRemove={() => setClassificationFilter('all')}
                  />
                )}
                {orgAuthFilter !== 'all' && (
                  <FilterChip
                    label={`Auth: ${orgAuthFilter === 'auth0' ? 'Auth0' : 'Legacy'}`}
                    onRemove={() => setOrgAuthFilter('all')}
                  />
                )}
                {orgPollingFilter !== 'all' && (
                  <FilterChip
                    label={`Polling: ${orgPollingFilter}`}
                    onRemove={() => setOrgPollingFilter('all')}
                  />
                )}
                {hasOrgFilters && (
                  <Button variant="ghost" size="sm" className="dls-h-6 dls-text-xs" onClick={clearOrgFilters}>
                    Clear all
                  </Button>
                )}
                <div className="dls-relative">
                  <Search className="dls-absolute dls-left-2.5 dls-top-1/2 -dls-translate-y-1/2 dls-h-3.5 dls-w-3.5 dls-text-muted-foreground" />
                  <Input placeholder="Search organizations..." value={search} onChange={e => setSearch(e.target.value)} className="dls-w-64 dls-pl-8 dls-h-9 dls-rounded-xl" />
                </div>
                <Button size="sm" variant="secondary" onClick={handleExportOrgsCsv} disabled={filteredOrgs.length === 0}>
                  <Download className="dls-h-3.5 dls-w-3.5 dls-mr-1.5" /> Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Bulk actions bar — mirrors the Pending Approvals pattern */}
            <div className="dls-flex dls-flex-wrap dls-items-center dls-gap-3 dls-mb-4 dls-pb-4 dls-border-b dls-border-border">
              <span className="dls-text-sm dls-text-muted-foreground">
                {orgSelectedIds.size > 0 ? `${orgSelectedIds.size} selected` : `${filteredOrgs.length} shown`}
              </span>
              <Button size="sm" variant="secondary" disabled={orgSelectedIds.size === 0 || orgBulkProcessing} onClick={() => handleBulkOrgStatus('active')}>
                <CheckCircle className="dls-h-3.5 dls-w-3.5 dls-mr-1" /> Activate
              </Button>
              <Button size="sm" variant="secondary" disabled={orgSelectedIds.size === 0 || orgBulkProcessing} onClick={() => handleBulkOrgStatus('disabled')}>
                <XCircle className="dls-h-3.5 dls-w-3.5 dls-mr-1" /> Disable
              </Button>
              <div className="dls-h-5 dls-w-px dls-bg-border" />
              <Select value={orgBulkClassification} onValueChange={setOrgBulkClassification}>
                <SelectTrigger className="dls-w-32 dls-h-8 dls-text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLASSIFICATION_OPTIONS.map(c => (
                    <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="secondary" disabled={orgSelectedIds.size === 0 || orgBulkProcessing} onClick={handleBulkOrgClassification}>
                Set Classification
              </Button>
              <div className="dls-h-5 dls-w-px dls-bg-border" />
              <Button size="sm" variant="secondary" disabled={orgSelectedIds.size === 0 || orgBulkProcessing} onClick={() => handleBulkOrgPolling(true)}>
                Enable Polling
              </Button>
              <Button size="sm" variant="secondary" disabled={orgSelectedIds.size === 0 || orgBulkProcessing} onClick={() => handleBulkOrgPolling(false)}>
                Disable Polling
              </Button>
              <div className="dls-h-5 dls-w-px dls-bg-border" />
              <Button size="sm" variant="secondary" disabled={orgSelectedIds.size === 0 || orgBulkProcessing} onClick={handleBulkOrgPoll}>
                {orgBulkProcessing ? <RefreshCw className="dls-h-3.5 dls-w-3.5 dls-mr-1 dls-animate-spin" /> : <Play className="dls-h-3.5 dls-w-3.5 dls-mr-1" />}
                Poll Now
              </Button>
              {orgBulkResult && (
                <span className="dls-text-xs dls-text-muted-foreground">{orgBulkResult}</span>
              )}
            </div>
            <div className="dls-max-h-[500px] dls-overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="dls-w-10">
                      <input
                        type="checkbox"
                        checked={filteredOrgs.length > 0 && orgSelectedIds.size === filteredOrgs.length}
                        onChange={toggleOrgSelectAll}
                        className="dls-rounded dls-border-border"
                      />
                    </TableHead>
                    <OrgSortableHeader field="name">Name</OrgSortableHeader>
                    <OrgSortableHeader field="domain">Domain</OrgSortableHeader>
                    {/* Status — sortable + filterable */}
                    <TableHead>
                      <div className="dls-flex dls-items-center dls-gap-1">
                        <button
                          type="button"
                          onClick={() => handleOrgSort('status')}
                          className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                        >
                          Status
                          <OrgSortIcon field="status" />
                        </button>
                        <Select
                          value={orgStatusFilter}
                          onValueChange={(v) => setOrgStatusFilter(v as OrgStatusFilter)}
                        >
                          <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                            <span className="dls-sr-only">Filter status</span>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="pending_approval">Pending Approval</SelectItem>
                            <SelectItem value="disabled">Disabled</SelectItem>
                            <SelectItem value="decommissioned">Decommissioned</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableHead>
                    {/* Classification — sortable + filterable */}
                    <TableHead>
                      <div className="dls-flex dls-items-center dls-gap-1">
                        <button
                          type="button"
                          onClick={() => handleOrgSort('classification')}
                          className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                        >
                          Classification
                          <OrgSortIcon field="classification" />
                        </button>
                        <Select
                          value={classificationFilter}
                          onValueChange={(v) => setClassificationFilter(v as ClassificationFilter)}
                        >
                          <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                            <span className="dls-sr-only">Filter classification</span>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            {CLASSIFICATION_OPTIONS.map(c => (
                              <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableHead>
                    {/* Auth Type — sortable + filterable */}
                    <TableHead>
                      <div className="dls-flex dls-items-center dls-gap-1">
                        <button
                          type="button"
                          onClick={() => handleOrgSort('authType')}
                          className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                        >
                          Auth Type
                          <OrgSortIcon field="authType" />
                        </button>
                        <Select
                          value={orgAuthFilter}
                          onValueChange={(v) => setOrgAuthFilter(v as OrgAuthFilter)}
                        >
                          <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                            <span className="dls-sr-only">Filter auth type</span>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="auth0">Auth0</SelectItem>
                            <SelectItem value="legacy">Legacy</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableHead>
                    <OrgSortableHeader field="nodes" className="dls-text-right">Nodes</OrgSortableHeader>
                    <OrgSortableHeader field="users" className="dls-text-right">Users</OrgSortableHeader>
                    {/* Polling — sortable + filterable */}
                    <TableHead>
                      <div className="dls-flex dls-items-center dls-gap-1">
                        <button
                          type="button"
                          onClick={() => handleOrgSort('polling')}
                          className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                        >
                          Polling
                          <OrgSortIcon field="polling" />
                        </button>
                        <Select
                          value={orgPollingFilter}
                          onValueChange={(v) => setOrgPollingFilter(v as OrgPollingFilter)}
                        >
                          <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                            <span className="dls-sr-only">Filter polling</span>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="enabled">Enabled</SelectItem>
                            <SelectItem value="disabled">Disabled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableHead>
                    <OrgSortableHeader field="lastPoll">Last Poll</OrgSortableHeader>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrgs.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="dls-text-center dls-py-12 dls-text-muted-foreground">No organizations found</TableCell></TableRow>
                  ) : (
                    filteredOrgs.map(org => (
                      <TableRow key={org.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={orgSelectedIds.has(org.id)}
                            onChange={() => toggleOrgSelect(org.id)}
                            className="dls-rounded dls-border-border"
                          />
                        </TableCell>
                        <TableCell className="dls-font-medium">
                          {org.name}
                          {org.removedFromSourceAt && (
                            <AlertTriangle className="dls-h-3.5 dls-w-3.5 dls-text-status-warning dls-ml-1.5 dls-inline" title={`Not found in Secure Edge API since ${formatDateTime(org.removedFromSourceAt)}`} />
                          )}
                        </TableCell>
                        <TableCell className="dls-text-muted-foreground dls-text-sm">{org.domainName || '–'}</TableCell>
                        <TableCell>
                          {/* Status toggle: active ↔ disabled */}
                          {org.status === 'active' || org.status === 'disabled' ? (
                            <Select
                              value={org.status}
                              onValueChange={(val) => handleStatusToggle(org, val as 'active' | 'disabled')}
                            >
                              <SelectTrigger className="dls-h-7 dls-w-24 dls-text-xs dls-border-0 dls-p-0 dls-font-normal">
                                <Badge variant="secondary" className={STATUS_BADGE_CLASSES[org.status]}>
                                  {org.status}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">active</SelectItem>
                                <SelectItem value="disabled">disabled</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="secondary" className={STATUS_BADGE_CLASSES[org.status]}>
                              {org.status.replace('_', ' ')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select value={org.classification} onValueChange={async (val) => {
                            await updateOrgClassification(org.id, val);
                            setOrganizations(prev => prev.map(o => o.id === org.id ? { ...o, classification: val as Organization['classification'] } : o));
                          }}>
                            <SelectTrigger className="dls-h-7 dls-w-24 dls-text-xs dls-border-0 dls-p-0 dls-font-normal"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="customer">customer</SelectItem>
                              <SelectItem value="partner">partner</SelectItem>
                              <SelectItem value="shared">shared</SelectItem>
                              <SelectItem value="internal">internal</SelectItem>
                              <SelectItem value="demo">demo</SelectItem>
                              <SelectItem value="unknown">unknown</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="dls-text-sm">{org.isCentralAuth ? 'Auth0' : 'Legacy'}</TableCell>
                        <TableCell className="dls-text-right dls-font-mono dls-text-sm">{org.nodeCount}</TableCell>
                        <TableCell className="dls-text-right dls-font-mono dls-text-sm">{org.userCount}</TableCell>
                        <TableCell>
                          {/* Polling toggle — disabled for non-active orgs */}
                          <button
                            onClick={async () => {
                              if (org.status !== 'active') return;
                              const newVal = !org.pollingEnabled;
                              try {
                                await updateOrgPolling(org.id, newVal);
                                setOrganizations(prev => prev.map(o => o.id === org.id ? { ...o, pollingEnabled: newVal } : o));
                              } catch {
                                // Error handling
                              }
                            }}
                            className={org.status === 'active' ? 'dls-cursor-pointer' : 'dls-cursor-not-allowed dls-opacity-50'}
                            title={org.status !== 'active' ? 'Enable polling requires active status' : org.pollingEnabled ? 'Click to stop polling' : 'Click to start polling'}
                          >
                            {org.pollingEnabled ? (
                              <CheckCircle className="dls-h-4 dls-w-4 dls-text-status-online" />
                            ) : (
                              <XCircle className="dls-h-4 dls-w-4 dls-text-muted-foreground" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell>
                          {org.lastSuccessfulPoll ? (
                            <div className="dls-flex dls-items-center dls-gap-1.5" title={`${formatDateTime(org.lastSuccessfulPoll)}${org.lastPollError ? `\nError: ${org.lastPollError}` : ''}`}>
                              <span className={`dls-h-1.5 dls-w-1.5 dls-rounded-full dls-flex-shrink-0 ${
                                org.lastPollError || (org.consecutivePollFailures ?? 0) > 0
                                  ? 'dls-bg-status-offline'
                                  : org.circuitBreakerOpenUntil && new Date(org.circuitBreakerOpenUntil) > new Date()
                                    ? 'dls-bg-status-warning'
                                    : 'dls-bg-status-online'
                              }`} />
                              <span className="dls-text-xs dls-text-muted-foreground dls-whitespace-nowrap">
                                {formatTimeAgo(org.lastSuccessfulPoll)}
                              </span>
                            </div>
                          ) : (
                            <span className="dls-text-xs dls-text-muted-foreground">–</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="dls-flex dls-gap-1">
                            <Button size="sm" variant="ghost" className="dls-h-7 dls-px-2 dls-text-xs" disabled={pollingOrgId === org.id} onClick={() => handlePollOrg(org.id, org.name)} title="Manual poll">
                              {pollingOrgId === org.id ? <RefreshCw className="dls-h-3.5 dls-w-3.5 dls-animate-spin" /> : <Play className="dls-h-3.5 dls-w-3.5" />}
                            </Button>
                            {(org.status === 'active' || org.status === 'disabled') && (
                              <Button size="sm" variant="ghost" className="dls-h-7 dls-px-2 dls-text-xs dls-text-status-offline" onClick={() => setDecommissionOrg(org)} title="Decommission organization">
                                <Trash2 className="dls-h-3.5 dls-w-3.5" />
                              </Button>
                            )}
                            {org.status === 'decommissioned' && (
                              <>
                                <Button size="sm" variant="ghost" className="dls-h-7 dls-px-2 dls-text-xs" onClick={() => handleExportOrg(org.id)} title="Export all data">
                                  <Download className="dls-h-3.5 dls-w-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" className="dls-h-7 dls-px-2 dls-text-xs dls-text-status-offline" onClick={() => { setPurgeOrg({ id: org.id, name: org.name }); setPurgeConfirmText(''); }} title="Purge all data">
                                  <XCircle className="dls-h-3.5 dls-w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {pollResult && (
              <div className="dls-mt-3 dls-text-sm dls-text-muted-foreground dls-bg-muted dls-rounded-md dls-px-3 dls-py-2">{pollResult}</div>
            )}
          </CardContent>
        </Card>

        {/* 5. Recent Polling Cycles (Telemetry) */}
        {pollingCycles.length > 0 && (
          <Card>
            <CardHeader>
              <div className="dls-flex dls-flex-col sm:dls-flex-row dls-items-start sm:dls-items-center dls-justify-between dls-gap-4">
                <CardTitle className="dls-flex dls-items-center dls-gap-2">
                  <RefreshCw className="dls-h-5 dls-w-5" />
                  Recent Polling Cycles
                  <span className="dls-text-sm dls-font-normal dls-text-muted-foreground">({pollingCyclesTotal > 0 ? pollingCyclesTotal : pollingCycles.length} total)</span>
                </CardTitle>
                <ExportMenu
                  onExportPdf={handleExportCyclesPdf}
                  onExportCsv={handleExportCyclesCsv}
                  label="Export"
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="dls-max-h-[400px] dls-overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cycle</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Tiers</TableHead>
                      <TableHead className="dls-text-right">Attempted</TableHead>
                      <TableHead className="dls-text-right">Succeeded</TableHead>
                      <TableHead className="dls-text-right">Failed</TableHead>
                      <TableHead className="dls-text-right">Deferred</TableHead>
                      <TableHead className="dls-text-right">API Calls</TableHead>
                      <TableHead>Trigger</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pollingCycles.map(cycle => (
                      <>
                        <TableRow
                          key={cycle.id}
                          className="dls-cursor-pointer hover:dls-bg-muted/50"
                          onClick={() => handleToggleCycleExpand(cycle.id)}
                        >
                          <TableCell className="dls-font-mono dls-text-sm">#{cycle.cycleNumber}</TableCell>
                          <TableCell className="dls-text-sm dls-text-muted-foreground dls-whitespace-nowrap">{formatDateTime(cycle.startedAt)}</TableCell>
                          <TableCell className="dls-text-sm dls-font-mono">{cycle.durationMs && cycle.durationMs > 0 ? `${(cycle.durationMs / 1000).toFixed(1)}s` : '–'}</TableCell>
                          <TableCell>
                            <div className="dls-flex dls-gap-1 dls-flex-wrap">
                              {cycle.tiersRun.map(t => (
                                <Badge key={t} variant="secondary" className="dls-text-xs">{t}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="dls-text-right dls-font-mono dls-text-sm">{cycle.orgsAttempted}</TableCell>
                          <TableCell className="dls-text-right dls-font-mono dls-text-sm dls-text-status-online">{cycle.orgsSucceeded}</TableCell>
                          <TableCell className="dls-text-right dls-font-mono dls-text-sm">{cycle.orgsFailed > 0 ? <span className="dls-text-status-offline">{cycle.orgsFailed}</span> : '0'}</TableCell>
                          <TableCell className="dls-text-right dls-font-mono dls-text-sm">{(cycle.orgsDeferred ?? 0) > 0 ? <span className="dls-text-status-warning">{cycle.orgsDeferred}</span> : '0'}</TableCell>
                          <TableCell className="dls-text-right dls-font-mono dls-text-sm">{cycle.apiCallsMade}</TableCell>
                          <TableCell><Badge variant="secondary" className="dls-text-xs">{cycle.trigger}</Badge></TableCell>
                        </TableRow>
                        {expandedCycleId === cycle.id && (
                          <TableRow key={`${cycle.id}-detail`}>
                            <TableCell colSpan={10} className="dls-p-0">
                              <div className="dls-bg-muted/30 dls-p-4 dls-border-y dls-border-border/50">
                                {loadingOrgResults ? (
                                  <p className="dls-text-sm dls-text-muted-foreground dls-text-center dls-py-4">Loading org results...</p>
                                ) : cycleOrgResults.length === 0 ? (
                                  <p className="dls-text-sm dls-text-muted-foreground dls-text-center dls-py-4">No per-org data available for this cycle (tracking added in v0.4.1).</p>
                                ) : (
                                  <div className="dls-max-h-[300px] dls-overflow-y-auto">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Organization</TableHead>
                                          <TableHead>Status</TableHead>
                                          <TableHead>Tiers</TableHead>
                                          <TableHead className="dls-text-right">Duration</TableHead>
                                          <TableHead className="dls-text-right">API Calls</TableHead>
                                          <TableHead>Error</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {cycleOrgResults.map(r => (
                                          <TableRow key={r.id}>
                                            <TableCell className="dls-text-sm">{r.orgName}</TableCell>
                                            <TableCell>
                                              <Badge variant="secondary" className={
                                                r.status === 'succeeded' ? 'dls-bg-status-online/10 dls-text-status-online' :
                                                r.status === 'failed' ? 'dls-bg-status-offline/10 dls-text-status-offline' :
                                                r.status === 'deferred' ? 'dls-bg-status-warning/10 dls-text-status-warning' :
                                                'dls-bg-muted dls-text-muted-foreground'
                                              }>
                                                {r.status}
                                              </Badge>
                                            </TableCell>
                                            <TableCell>
                                              <div className="dls-flex dls-gap-1 dls-flex-wrap">
                                                {(r.tiersRun ?? []).map(t => (
                                                  <Badge key={t} variant="secondary" className="dls-text-[10px]">{t}</Badge>
                                                ))}
                                                {(!r.tiersRun || r.tiersRun.length === 0) && <span className="dls-text-xs dls-text-muted-foreground">–</span>}
                                              </div>
                                            </TableCell>
                                            <TableCell className="dls-text-right dls-font-mono dls-text-sm">{r.durationMs ? `${(r.durationMs / 1000).toFixed(1)}s` : '–'}</TableCell>
                                            <TableCell className="dls-text-right dls-font-mono dls-text-sm">{r.apiCalls}</TableCell>
                                            <TableCell className="dls-text-xs dls-text-muted-foreground dls-max-w-[200px] dls-truncate">{r.error || '–'}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {pollingCycles.length < pollingCyclesTotal && (
                <div className="dls-flex dls-items-center dls-justify-center dls-pt-3 dls-border-t dls-border-border/50 dls-mt-2">
                  <Button variant="ghost" size="sm" onClick={handleLoadMoreCycles}>
                    Load More ({pollingCyclesTotal - pollingCycles.length} remaining)
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 6. Audit Trail Card — bounded height */}
        <Card>
          <CardHeader>
            <div className="dls-flex dls-flex-col sm:dls-flex-row dls-items-start sm:dls-items-center dls-justify-between dls-gap-4">
              <CardTitle className="dls-flex dls-items-center dls-gap-2">
                <FileText className="dls-h-5 dls-w-5" />
                Audit Trail
                {auditTotal > 0 && <span className="dls-text-sm dls-font-normal dls-text-muted-foreground">({auditTotal} total)</span>}
              </CardTitle>
              <Button size="sm" variant="secondary" onClick={() => window.open(getAuditExportUrl(), '_blank')}>
                <Download className="dls-h-3.5 dls-w-3.5 dls-mr-1.5" /> Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {auditLogs.length === 0 ? (
              <div className="dls-text-center dls-py-12 dls-text-muted-foreground dls-text-sm">No audit entries yet.</div>
            ) : (
              <div className="dls-max-h-[400px] dls-overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Target Org</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map(entry => (
                      <TableRow key={entry.id}>
                        <TableCell className="dls-text-sm dls-text-muted-foreground dls-whitespace-nowrap">{formatDateTime(entry.createdAt)}</TableCell>
                        <TableCell className="dls-text-sm">
                          <div className="dls-font-medium">{entry.name}</div>
                          <div className="dls-text-xs dls-text-muted-foreground">{entry.email}</div>
                        </TableCell>
                        <TableCell><Badge variant="secondary" className="dls-text-xs">{entry.action.replace(/_/g, ' ')}</Badge></TableCell>
                        <TableCell className="dls-text-sm">{entry.targetOrgName || '–'}</TableCell>
                        <TableCell className="dls-text-xs dls-text-muted-foreground dls-max-w-[200px] dls-truncate">{entry.details || '–'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 7. API Keys & Webhooks */}
        <ApiKeysManager />
      </div>

      {/* Reactivation Dialog */}
      <Dialog open={!!reactivateOrg} onOpenChange={(open) => { if (!open) setReactivateOrg(null); }}>
        <DialogContent className="sm:dls-max-w-md">
          <DialogHeader>
            <DialogTitle>Organization Reactivated</DialogTitle>
            <DialogDescription>
              {reactivateOrg?.name} has been set to active. Would you also like to enable polling for this organization?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => handleReactivateResponse(false)}>Not Now</Button>
            <Button onClick={() => handleReactivateResponse(true)}>Enable Polling</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decommission confirmation dialog */}
      <Dialog open={!!decommissionOrg} onOpenChange={(open) => { if (!open) setDecommissionOrg(null); }}>
        <DialogContent className="sm:dls-max-w-md">
          <DialogHeader>
            <DialogTitle>Decommission Organization</DialogTitle>
            <DialogDescription>
              Are you sure you want to decommission <strong>{decommissionOrg?.name}</strong>? This will permanently disable polling. The organization can then be exported and purged. This action cannot be reversed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDecommissionOrg(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDecommission}>Decommission</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge confirmation dialog — type org name to confirm */}
      <Dialog open={!!purgeOrg} onOpenChange={(open) => { if (!open) { setPurgeOrg(null); setPurgeConfirmText(''); } }}>
        <DialogContent className="sm:dls-max-w-md">
          <DialogHeader>
            <DialogTitle className="dls-text-status-offline">Purge Organization Data</DialogTitle>
            <DialogDescription>
              This will <strong>permanently delete all data</strong> for <strong>{purgeOrg?.name}</strong> including nodes, networks, services, users, state history, and audit logs. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="dls-py-2">
            <label className="dls-text-sm dls-font-medium">Type <strong>{purgeOrg?.name}</strong> to confirm:</label>
            <Input
              value={purgeConfirmText}
              onChange={(e) => setPurgeConfirmText(e.target.value)}
              placeholder={purgeOrg?.name}
              className="dls-mt-2"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setPurgeOrg(null); setPurgeConfirmText(''); }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handlePurgeConfirm}
              disabled={purgeConfirmText !== purgeOrg?.name || purging}
            >
              {purging ? 'Purging...' : 'Permanently Delete All Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Per-user developer preferences — slicer for the chart library used on the
 * Analytics dashboards, and the Preview-as-consumer toggle. Both persist to
 * localStorage. Visible to any role==='dev' user (Dev Console gate), never
 * subject to feature flags.
 */
const PREVIEW_STAGE_OPTIONS: Array<{
  value: PreviewStage;
  label: string;
  description: string;
}> = [
  { value: 'full', label: 'Full', description: '@neeve.ai employee — see everything' },
  { value: 'deep', label: 'Deep', description: 'Paying Deep Insights tenant — mvp + base + deep' },
  { value: 'base', label: 'Base', description: 'Base-unlocked tenant — mvp + base' },
  { value: 'mvp', label: 'MVP', description: 'MVP-only consumer — only mvp-stage features' },
];

function DeveloperPreferencesCard() {
  const [chartLib, setChartLibRaw] = useChartLib();
  const { previewStage, setPreviewStage } = useFeatureVisibility();
  const [recentLibChange, setRecentLibChange] = useState<ChartLib | null>(null);

  // Wrap setChartLib so changing the slicer flashes a "Switched to {lib}"
  // confirmation under the control. Adds visible feedback in addition to the
  // per-chart "rendered via {lib}" corner badge.
  const setChartLib = (next: ChartLib) => {
    setChartLibRaw(next);
    setRecentLibChange(next);
  };
  useEffect(() => {
    if (recentLibChange === null) return;
    const t = setTimeout(() => setRecentLibChange(null), 2500);
    return () => clearTimeout(t);
  }, [recentLibChange]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="dls-flex dls-items-center dls-gap-2">
          <Settings className="dls-h-5 dls-w-5 dls-text-neeve-primary" />
          Developer Preferences
        </CardTitle>
      </CardHeader>
      <CardContent className="dls-space-y-6">
        {/* Chart library slicer — title + description stacked above the
            control so the description gets the full card width and doesn't
            squeeze into a narrow column beside the dropdown. */}
        <div className="dls-flex dls-flex-col dls-gap-2">
          <div className="dls-flex dls-items-center dls-justify-between dls-gap-3">
            <div className="dls-text-sm dls-font-medium dls-text-foreground">Chart library</div>
            <Select value={chartLib} onValueChange={(v) => setChartLib(v as ChartLib)}>
              <SelectTrigger className="dls-w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHART_LIBS.map((lib) => (
                  <SelectItem key={lib} value={lib}>
                    {lib.charAt(0).toUpperCase() + lib.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="dls-text-xs dls-text-muted-foreground">
            Choose which library renders charts on the Analytics dashboards. Recharts is the default;
            switching swaps every chart on /analytics/* in place. A small "rendered via …" badge
            appears in the corner of each chart whenever the slicer is in a non-default state, so
            you can verify the swap took effect. Affects only your browser.
          </p>
          {recentLibChange && (
            <div className="dls-text-xs dls-text-status-online dls-font-medium dls-animate-in dls-fade-in">
              ✓ Switched to {recentLibChange.charAt(0).toUpperCase() + recentLibChange.slice(1)} — open any /analytics page to see the swap
            </div>
          )}
        </div>

        {/* Preview stage chips — 4-segment control replacing the binary toggle.
            Lets a dev flip the UI between viewing-as-tier without re-issuing
            the JWT. Persists to localStorage. Dev Console itself is never gated. */}
        <div className="dls-flex dls-flex-col dls-gap-2">
          <div className="dls-flex dls-items-center dls-justify-between dls-gap-3">
            <div className="dls-text-sm dls-font-medium dls-text-foreground">Preview as</div>
            <div className="dls-inline-flex dls-rounded-lg dls-border dls-border-border dls-overflow-hidden">
              {PREVIEW_STAGE_OPTIONS.map((opt) => {
                const active = previewStage === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPreviewStage(opt.value)}
                    title={opt.description}
                    aria-pressed={active}
                    className={cn(
                      'dls-px-3 dls-py-1.5 dls-text-xs dls-font-medium dls-transition-colors',
                      active
                        ? 'dls-bg-neeve-primary dls-text-white'
                        : 'dls-bg-white dls-text-muted-foreground hover:dls-bg-muted/40 hover:dls-text-foreground'
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="dls-text-xs dls-text-muted-foreground">
            Preview the UI as if you were a user in the selected tier. <strong>Full</strong> is the
            normal employee view (see everything). <strong>Deep</strong> simulates a paying Deep
            Insights tenant. <strong>Base</strong> simulates a Base-unlocked tenant. <strong>MVP</strong>
            simulates an MVP-only consumer (most restrictive). The Dev Console stays visible regardless.
            Affects only your browser.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
